/**
 * InstallPrompt Component
 * Prompts users to install the PWA on their device
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Slide,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  GetApp as InstallIcon,
  Close as CloseIcon,
  PhoneIphone as PhoneIcon,
  Computer as DesktopIcon,
} from '@mui/icons-material';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const InstallPrompt: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Check if dismissed recently (within 7 days)
    const dismissedDate = localStorage.getItem('clinafy_pwa_dismissed');
    if (dismissedDate) {
      const daysSinceDismissed = (Date.now() - parseInt(dismissedDate)) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) {
        return;
      }
    }

    // Check for iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    const isInStandaloneMode = ('standalone' in window.navigator) && (window.navigator as any).standalone;

    if (isIOSDevice && !isInStandaloneMode) {
      setIsIOS(true);
      // Show iOS prompt after a delay
      setTimeout(() => setShowPrompt(true), 5000);
      return;
    }

    // Listen for beforeinstallprompt event (Chrome, Edge, etc.)
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      // Show prompt after user has been on the page for a bit
      setTimeout(() => setShowPrompt(true), 30000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Listen for app installed event
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setShowPrompt(false);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        setIsInstalled(true);
      }

      setDeferredPrompt(null);
      setShowPrompt(false);
    } catch (error) {
      console.error('Error installing PWA:', error);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setShowIOSInstructions(false);
    localStorage.setItem('clinafy_pwa_dismissed', Date.now().toString());
  };

  const handleShowIOSInstructions = () => {
    setShowIOSInstructions(true);
  };

  // Don't render if installed or no prompt available
  if (isInstalled || (!deferredPrompt && !isIOS)) {
    return null;
  }

  return (
    <>
      {/* Install Prompt Banner */}
      <Slide direction="up" in={showPrompt && !showIOSInstructions} mountOnEnter unmountOnExit>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: isMobile ? 0 : 20,
            left: isMobile ? 0 : '50%',
            transform: isMobile ? 'none' : 'translateX(-50%)',
            right: isMobile ? 0 : 'auto',
            width: isMobile ? '100%' : 'auto',
            maxWidth: 400,
            p: 2,
            m: isMobile ? 0 : 2,
            borderRadius: isMobile ? '16px 16px 0 0' : 3,
            background: 'linear-gradient(135deg, #1B6A63 0%, #2E8C80 100%)',
            color: 'white',
            zIndex: 9999,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {isMobile ? <PhoneIcon /> : <DesktopIcon />}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                Instalar Clinafy
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mb: 2 }}>
                Acesse mais rápido e use offline! Adicione a Clinafy a sua tela inicial.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {isIOS ? (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleShowIOSInstructions}
                    sx={{
                      bgcolor: 'white',
                      color: '#1B6A63',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                    }}
                    startIcon={<InstallIcon />}
                  >
                    Como instalar
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleInstallClick}
                    sx={{
                      bgcolor: 'white',
                      color: '#1B6A63',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                    }}
                    startIcon={<InstallIcon />}
                  >
                    Instalar
                  </Button>
                )}
                <Button
                  variant="text"
                  size="small"
                  onClick={handleDismiss}
                  sx={{ color: 'rgba(255,255,255,0.8)' }}
                >
                  Agora não
                </Button>
              </Box>
            </Box>
            <IconButton
              size="small"
              onClick={handleDismiss}
              sx={{ color: 'rgba(255,255,255,0.7)', mt: -1, mr: -1 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Paper>
      </Slide>

      {/* iOS Instructions Modal */}
      <Slide direction="up" in={showIOSInstructions} mountOnEnter unmountOnExit>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            p: 3,
            borderRadius: '20px 20px 0 0',
            zIndex: 9999,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Instalar no iPhone/iPad
            </Typography>
            <IconButton onClick={handleDismiss}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: '#1B6A63',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 600,
                }}
              >
                1
              </Box>
              <Typography>
                Toque no botao <strong>Compartilhar</strong> (quadrado com seta para cima)
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: '#1B6A63',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 600,
                }}
              >
                2
              </Box>
              <Typography>
                Role para baixo e toque em <strong>Adicionar a Tela de Início</strong>
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: '#1B6A63',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 600,
                }}
              >
                3
              </Box>
              <Typography>
                Toque em <strong>Adicionar</strong> no canto superior direito
              </Typography>
            </Box>
          </Box>

          <Button
            fullWidth
            variant="contained"
            onClick={handleDismiss}
            sx={{
              mt: 3,
              bgcolor: '#1B6A63',
              '&:hover': { bgcolor: '#155a54' },
            }}
          >
            Entendi
          </Button>
        </Paper>
      </Slide>
    </>
  );
};

export default InstallPrompt;
